import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  Toaster,
  teamsDarkTheme,
  useId,
  useToastController,
} from "@fluentui/react-components";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCollections } from "../../store/items/things";
import { MdAdd, MdClose, MdCollectionsBookmark } from "react-icons/md";
import { BsCheck } from "react-icons/bs";
import {
  RegExpMatcher,
  englishDataset,
  englishRecommendedTransformers,
} from "obscenity";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../hooks/useFirebase";
import { FaCrown } from "react-icons/fa";

const hasBadWord = (text) => {
  // check for profanity
  const matcher = new RegExpMatcher({
    ...englishDataset.build(),
    ...englishRecommendedTransformers,
  });

  if (matcher.hasMatch(text)) {
    return true;
  }

  return false;
};

const CreateCollectionModal = ({
  showModal,
  setShowModal,
  normalCollection,
  fetchNormalCollection,
}) => {
  const dispatch = useDispatch();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);
  const userCollections = useSelector((state) => state.things.collections);
  const revenueCat = useSelector((state) => state.user.revenueCat);

  const [nameText, setNameText] = useState(normalCollection?.title || "");
  const [tagInput, setTagInput] = useState("");

  const getDefaultTags = useCallback(() => {
    if (normalCollection?.collectionTags) {
      return normalCollection.collectionTags.map((tag) => tag.tag_id);
    } else {
      return [];
    }
  }, [normalCollection]);

  const [tagsList, setTagsList] = useState(getDefaultTags());
  const [isPrivate, setIsPrivate] = useState(false);

  const [updatingCollection, setUpdatingCollection] = useState(false);

  const onOpenChange = useCallback(
    (event, data) => {
      if (updatingCollection) {
        return;
      }
      setTagsList(getDefaultTags());
      setNameText(normalCollection?.title || "");
      setTagInput("");
      setShowModal(data.open);
    },
    [updatingCollection, getDefaultTags, normalCollection, setShowModal]
  );

  const handleAddTag = useCallback(() => {
    if (!tagInput.length) return;
    const cleanTag = tagInput
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (!cleanTag) return;
    if (hasBadWord(cleanTag)) {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle>No bad words please</ToastTitle>
        </Toast>,
        { position: "bottom", intent: "warning" }
      );
      return;
    }
    if (tagsList.includes(cleanTag)) {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle>Tag already exists</ToastTitle>
        </Toast>,
        { position: "bottom", intent: "warning" }
      );
      return;
    }
    setTagsList((prev) => [...prev, cleanTag]);
    setTagInput("");
  }, [dispatchToast, tagInput, tagsList]);

  const handleCreateCollection = useCallback(async () => {
    if (!nameText || nameText.trim() === "") {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle>Name is required</ToastTitle>
        </Toast>,
        { position: "bottom", intent: "warning" }
      );
      return;
    } else if (hasBadWord(nameText)) {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle>No bad words please</ToastTitle>
        </Toast>,
        { position: "bottom", intent: "warning" }
      );
      return;
    } else if (tagsList.length === 0) {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle>At least 1 tag is required</ToastTitle>
        </Toast>,
        { position: "bottom", intent: "warning" }
      );
      return;
    }

    setUpdatingCollection(true);
    const collectionRef = doc(db, "Collections", normalCollection?.uid);
    // Update the original collection document
    await setDoc(
      collectionRef,
      {
        title: nameText,
        isPrivate: isPrivate,
        updated_at: new Date(),
      },
      { merge: true }
    );

    // Delete all current tag joins
    const tagJoinQuerySnapshot = await getDocs(
      query(
        collection(db, "CollectionTagJoinTable"),
        where("collectionId", "==", normalCollection?.uid)
      )
    );

    const deletePromises = tagJoinQuerySnapshot.docs.map((doc) =>
      deleteDoc(doc.ref)
    );
    await Promise.all(deletePromises);

    const tagPromises = tagsList.map(async (tag) => {
      const tagDocRef = doc(db, "CollectionTags", tag);
      let tagSnap = await getDoc(tagDocRef);

      // If the tag does not exist, create it
      if (!tagSnap.exists()) {
        await setDoc(tagDocRef, {
          slug: tag,
          created_at: new Date(),
        });
      }

      await addDoc(collection(db, "CollectionTagJoinTable"), {
        collectionId: normalCollection.uid,
        tag_id: tag, // Directly use 'tag' since it's equivalent to the document ID
        created_at: new Date(),
        title: nameText,
      });
    });

    await Promise.all(tagPromises);

    // Update Redux
    dispatch(
      setCollections(
        userCollections.map((collection) =>
          collection.uid === normalCollection.uid
            ? { ...collection, title: nameText, isPrivate }
            : collection
        )
      )
    );

    fetchNormalCollection();
    setShowModal(null);
    setUpdatingCollection(false);
    setTagInput("");
  }, [
    dispatch,
    dispatchToast,
    fetchNormalCollection,
    isPrivate,
    nameText,
    normalCollection,
    setShowModal,
    tagsList,
    userCollections,
  ]);

  useEffect(() => {
    if (normalCollection) {
      setNameText(normalCollection.title);
      setTagsList(getDefaultTags());
      setIsPrivate(normalCollection.isPrivate);
    }
  }, [getDefaultTags, normalCollection]);

  return (
    <Dialog open={showModal} onOpenChange={onOpenChange}>
      <DialogSurface
        style={{
          backgroundColor: teamsDarkTheme.colorNeutralBackground3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          // full height for mobile here
        }}
      >
        <DialogBody>
          <DialogTitle
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              borderRadius: "5px",
              padding: "10px 10px",
              boxShadow: teamsDarkTheme.shadow2,
              backgroundColor: teamsDarkTheme.colorNeutralBackground1,
              flexShrink: 1,
            }}
          >
            <MdCollectionsBookmark />
            <Text
              style={{
                fontSize: "20px",
              }}
              weight="bold"
            >
              Edit Collection
            </Text>
          </DialogTitle>
          <DialogContent
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <Text weight="bold">Name:</Text>
              <Input
                placeholder="Type Name..."
                onChange={(e, data) => setNameText(data.value)}
                value={nameText}
                style={{
                  backgroundColor: teamsDarkTheme.colorNeutralBackground1,
                  display: "flex",
                  flex: 1,
                }}
              />
            </div>
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <Text weight="bold">Add Tags:</Text>
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Input
                  //   appearance="underline"
                  placeholder="Type Tag..."
                  onChange={(e, data) => setTagInput(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  value={tagInput}
                  style={{
                    backgroundColor: teamsDarkTheme.colorNeutralBackground1,
                    display: "flex",
                    flex: 1,
                  }}
                />
                <Button
                  appearance="primary"
                  onClick={handleAddTag}
                  icon={<MdAdd />}
                >
                  Add
                </Button>
              </div>
            </div>
            <Text weight="bold">Tags:</Text>
            <div
              id="tag-wrap"
              style={{
                display: "flex",
                width: "100%",
                minHeight: "100px",
                borderRadius: "5px",
                boxShadow: teamsDarkTheme.shadow2,
                padding: "10px",
                boxSizing: "border-box",
                flexWrap: "wrap",
                gap: "10px",
                backgroundColor: teamsDarkTheme.colorNeutralBackground1,
              }}
            >
              {tagsList.length ? (
                <>
                  {tagsList.map((tag, i) => {
                    return (
                      <Button
                        style={{
                          height: "min-content",
                          width: "mmin-content",
                        }}
                        key={i}
                        icon={<MdClose />}
                        iconPosition="after"
                        onClick={() => {
                          setTagsList((v) => v.filter((t) => t !== tag));
                        }}
                      >
                        <Text>{tag}</Text>
                      </Button>
                    );
                  })}
                </>
              ) : (
                <Text style={{ opacity: 0.6 }}>
                  *At least 1 tag required...
                </Text>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                opacity: !revenueCat?.entitlements?.pro ? 0.5 : 1,
              }}
            >
              <Checkbox
                checked={isPrivate}
                onChange={() => setIsPrivate((checked) => !checked)}
                disabled={!revenueCat?.entitlements?.pro}
              />
              <Text>Private Collection</Text>
              {!revenueCat?.entitlements?.pro && (
                <FaCrown style={{ paddingLeft: "5px" }} />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Close</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              icon={
                updatingCollection ? (
                  <Spinner size="tiny" appearance="inverted" />
                ) : (
                  <BsCheck />
                )
              }
              onClick={handleCreateCollection}
            >
              Submit Changes
            </Button>
          </DialogActions>
        </DialogBody>
        <Toaster toasterId={toasterId} />
      </DialogSurface>
    </Dialog>
  );
};

export default React.memo(CreateCollectionModal);
